import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, Bot, ShieldCheck, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type QuickAction = {
  label: string;
  to: string;
  variant?: 'default' | 'outline' | 'secondary';
};

interface FinanceWorkspaceShellProps {
  title: string;
  subtitle: string;
  Icon: LucideIcon;
  iconClassName?: string;
  headerActions?: ReactNode;
  moduleBadge?: string;
  domainBadge?: string;
  agentTitle: string;
  agentSummary: string;
  checklistTitle: string;
  checklistStatus: string;
  checklistNote: string;
  checklistItems?: string[];
  quickActions: QuickAction[];
  children: ReactNode;
}

export function FinanceWorkspaceShell({
  title,
  subtitle,
  Icon,
  iconClassName,
  headerActions,
  moduleBadge,
  domainBadge,
  agentTitle,
  agentSummary,
  checklistTitle,
  checklistStatus,
  checklistNote,
  checklistItems = [],
  quickActions,
  children,
}: FinanceWorkspaceShellProps) {
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border bg-background shadow-sm">
            <Icon className={cn('h-5 w-5', iconClassName)} />
          </div>
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold">{title}</h1>
              <Badge className="border-sky-200 bg-sky-50 text-sky-700" variant="outline">
                AI-agentic native
              </Badge>
              {moduleBadge ? (
                <Badge className="border-border bg-muted/60 text-muted-foreground" variant="outline">
                  {moduleBadge}
                </Badge>
              ) : null}
              {domainBadge ? (
                <Badge className="border-border bg-muted/60 text-muted-foreground" variant="outline">
                  {domainBadge}
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {headerActions ? <div className="flex flex-wrap gap-2">{headerActions}</div> : null}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50">
          <CardContent className="space-y-4 p-4 md:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-sky-700">
                  <Bot className="h-3.5 w-3.5" />
                  Finance control surface
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{agentTitle}</h2>
                  <p className="text-sm leading-6 text-slate-600">{agentSummary}</p>
                </div>
              </div>
              <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700" variant="outline">
                <ShieldCheck className="mr-1 h-3 w-3" />
                Human approval stays
              </Badge>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-sky-100 bg-white/90 p-3">
                <div className="flex items-center gap-2 text-xs font-medium text-sky-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Watch continuously
                </div>
                <p className="mt-2 text-sm text-slate-600">The page is positioned for exception-first finance operations, not static month-end reporting.</p>
              </div>
              <div className="rounded-2xl border border-sky-100 bg-white/90 p-3">
                <div className="flex items-center gap-2 text-xs font-medium text-sky-700">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Explain controls
                </div>
                <p className="mt-2 text-sm text-slate-600">Every workspace links outward to reports, audit findings, and the next operational handoff surface.</p>
              </div>
              <div className="rounded-2xl border border-sky-100 bg-white/90 p-3">
                <div className="flex items-center gap-2 text-xs font-medium text-sky-700">
                  <ArrowRight className="h-3.5 w-3.5" />
                  Drive action
                </div>
                <p className="mt-2 text-sm text-slate-600">Operators should be able to spot risk, open the right report, and continue the workflow without losing context.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {quickActions.map((action) => (
                <Button key={`${action.to}-${action.label}`} asChild size="sm" variant={action.variant ?? 'outline'}>
                  <Link to={action.to}>
                    {action.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="space-y-3 p-4 md:p-5">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-amber-700">Checklist Repair</p>
                <h2 className="mt-1 text-base font-semibold text-slate-900">{checklistTitle}</h2>
              </div>
              <Badge className="border-amber-200 bg-white text-amber-700" variant="outline">
                {checklistStatus}
              </Badge>
            </div>
            <p className="text-sm leading-6 text-slate-600">{checklistNote}</p>
            {checklistItems.length > 0 ? (
              <div className="space-y-2">
                {checklistItems.map((item) => (
                  <div key={item} className="rounded-xl border border-amber-100 bg-white/90 px-3 py-2 text-sm text-slate-600">
                    {item}
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {children}
    </div>
  );
}